import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils/cn'
import type { VenueReservationConditionState } from '@/lib/venue/reservationSettingTypes'
import { reservationConditionTargetPath } from '@/lib/venue/reservationSettingViewModel'

/**
 * 활성화 조건 3종 체크리스트(#758 설계 §2 "UI 흐름" 3).
 *
 * 🚨 서버가 준 순서·개수를 그대로 렌더한다 — 미충족만 걸러내거나 재정렬하지 않는다(#757 §1 "미충족만
 * 내려보내지 않는 이유"와 대칭: 화면도 "받은 걸 그대로" 보여줘야 새 조건이 추가돼도 조용히 누락되지
 * 않는다). 미충족 행에만 이동 버튼을 붙인다 — 조건별로 개별 제공한다(#758 자율 결정 4).
 */
export function ReservationSettingConditionList({
  venueId,
  conditions,
}: {
  venueId: string
  conditions: VenueReservationConditionState[]
}) {
  return (
    <ul className="space-y-3">
      {conditions.map((condition) => (
        <ConditionRow key={condition.condition} venueId={venueId} condition={condition} />
      ))}
    </ul>
  )
}

function ConditionRow({
  venueId,
  condition,
}: {
  venueId: string
  condition: VenueReservationConditionState
}) {
  const targetPath = reservationConditionTargetPath(condition.condition)

  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border border-slateish-200 px-4 py-3">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            condition.satisfied ? 'bg-success-500' : 'bg-slateish-300'
          )}
        />
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-ink-700">{condition.label}</p>
            <Badge tone={condition.satisfied ? 'success' : 'neutral'}>
              {condition.satisfied ? '완료' : '미완료'}
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-slateish-600">{condition.description}</p>
        </div>
      </div>

      {!condition.satisfied && targetPath ? (
        <Link
          to={`/venues/${venueId}/${targetPath}`}
          className="shrink-0 rounded-lg border border-brand-500 px-3 py-1.5 text-xs font-medium text-brand-500 hover:bg-brand-50"
        >
          이동
        </Link>
      ) : null}
    </li>
  )
}
